/**
 * Generate Faraid calculation report as PDF.
 * Labels come from HEIR_DEFINITIONS / calculation result — no duplicate heir strings.
 */

import { jsPDF } from 'jspdf';
import type {
  AssetsState,
  DeceasedInfo,
  FaraidCalculationResult,
  HeirInput,
  HeirType,
  ShareResult,
} from '../types';
import { ASSET_TYPE_LABELS, FRACTION_LABELS } from '../types';
import { HEIR_DEFINITIONS, HEIRS_BY_CATEGORY } from '../types/heirDefinitions';
import { formatCurrency, formatFraction } from './faraidCalculator';
import { drawPdfWatermark } from './drawPdfWatermark';

export interface GenerateFaraidPdfInput {
  deceased: DeceasedInfo;
  heirs: Record<HeirType, HeirInput>;
  assets: AssetsState;
  result: FaraidCalculationResult;
}

const MARGIN = 20;
const CONTENT_WIDTH = 170;
const PAGE_WIDTH = 210;
const PAGE_HEIGHT = 297;
const PAGE_BOTTOM = PAGE_HEIGHT - MARGIN;
const LINE_HEIGHT = 6;
const CELL_PADDING = 2;
const ROW_GAP = 2;
const PENGIRAAN_COL_WIDTHS = [72, 32, 28, 38] as const;

interface PengiraanColumnPositions {
  warisW: number;
  bahagianX: number;
  pecahanX: number;
  jumlahX: number;
  warisMaxWidth: number;
}

interface PengiraanPdfRow {
  displayLabel: string;
  bahagian: string;
  fraction: string;
  amount: number;
}

function getPengiraanColumnPositions(startX: number): PengiraanColumnPositions {
  const [warisW, bahagianW, pecahanW] = PENGIRAAN_COL_WIDTHS;
  const bahagianX = startX + warisW;
  const pecahanX = bahagianX + bahagianW;
  const jumlahX = pecahanX + pecahanW;
  return {
    warisW,
    bahagianX,
    pecahanX,
    jumlahX,
    warisMaxWidth: warisW - CELL_PADDING,
  };
}

function measureWrappedTextHeight(
  doc: jsPDF,
  text: string,
  maxWidth: number
): number {
  const lines = doc.splitTextToSize(text, maxWidth);
  return lines.length * LINE_HEIGHT;
}

function drawWrappedText(
  doc: jsPDF,
  text: string,
  x: number,
  y: number,
  maxWidth: number
): number {
  const lines = doc.splitTextToSize(text, maxWidth);
  lines.forEach((line: string, index: number) => {
    doc.text(line, x, y + index * LINE_HEIGHT);
  });
  return y + lines.length * LINE_HEIGHT;
}

function drawPengiraanRow(
  doc: jsPDF,
  row: PengiraanPdfRow,
  startX: number,
  cols: PengiraanColumnPositions,
  y: number
): number {
  const warisLines = doc.splitTextToSize(row.displayLabel, cols.warisMaxWidth);
  warisLines.forEach((line: string, index: number) => {
    doc.text(line, startX, y + index * LINE_HEIGHT);
  });
  doc.text(row.bahagian, cols.bahagianX, y);
  doc.text(row.fraction, cols.pecahanX, y);
  doc.text(formatCurrency(row.amount), cols.jumlahX, y);
  return y + warisLines.length * LINE_HEIGHT;
}

function getBahagianLabel(share: ShareResult): string {
  if (share.fixedShare === '1/3_baki') return '1/3 Baki';
  if (share.fixedShare) return FRACTION_LABELS[share.fixedShare];
  return 'Asobah';
}

function getShareFractionLabel(share: ShareResult): string {
  if (share.count > 1) {
    return formatFraction(
      share.fractionNumerator,
      share.fractionDenominator * share.count
    );
  }
  return formatFraction(share.fractionNumerator, share.fractionDenominator);
}

function expandPengiraanRows(result: FaraidCalculationResult): PengiraanPdfRow[] {
  const rows: PengiraanPdfRow[] = [];

  for (const share of result.shares) {
    if (share.isBlocked) continue;

    const bahagian = getBahagianLabel(share);
    const fraction = getShareFractionLabel(share);

    if (share.count > 1) {
      for (let i = 0; i < share.count; i++) {
        rows.push({
          displayLabel: `${share.heirLabel} ${i + 1}`,
          bahagian,
          fraction: formatFraction(
            share.fractionNumerator,
            share.fractionDenominator
          ),
          amount: share.amountPerHeir,
        });
      }
    } else {
      rows.push({
        displayLabel: share.heirLabel,
        bahagian,
        fraction,
        amount: share.totalAmount,
      });
    }
  }

  return rows;
}

function getPresentHeirs(heirs: Record<HeirType, HeirInput>): { label: string; count: number }[] {
  const present: { label: string; count: number }[] = [];
  const categories = ['waris_utama', 'waris_ganti', 'waris_kedua'] as const;

  for (const category of categories) {
    for (const type of HEIRS_BY_CATEGORY[category]) {
      const input = heirs[type];
      if (!input?.isPresent || input.count <= 0) continue;
      const def = HEIR_DEFINITIONS[type];
      const countLabel = input.count > 1 ? ` (${input.count})` : '';
      present.push({
        label: `${def.labelMs}${countLabel}`,
        count: input.count,
      });
    }
  }

  return present;
}

function getAssetsWithValue(assets: AssetsState) {
  return assets.assets.filter((asset) => asset.value > 0);
}

function formatDeceasedGender(deceased: DeceasedInfo): string {
  return deceased.gender === 'lelaki' ? 'Lelaki' : 'Perempuan';
}

function formatPdfDate(date: Date): string {
  return date.toLocaleString('ms-MY', {
    day: '2-digit',
    month: 'long',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

export function generateFaraidPdf({
  deceased,
  heirs,
  assets,
  result,
}: GenerateFaraidPdfInput): void {
  const doc = new jsPDF({ unit: 'mm', format: 'a4' });
  let y = MARGIN;

  const ensureSpace = (neededHeight: number) => {
    if (y + neededHeight > PAGE_BOTTOM) {
      doc.addPage();
      drawPdfWatermark(doc, PAGE_WIDTH, PAGE_HEIGHT);
      y = MARGIN;
    }
  };

  const addSectionTitle = (title: string) => {
    ensureSpace(LINE_HEIGHT * 2);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(12);
    doc.text(title, MARGIN, y);
    y += LINE_HEIGHT * 1.5;
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(10);
  };

  const addText = (text: string, indent = 0) => {
    const maxWidth = CONTENT_WIDTH - indent;
    const height = measureWrappedTextHeight(doc, text, maxWidth);
    ensureSpace(height + ROW_GAP);
    y = drawWrappedText(doc, text, MARGIN + indent, y, maxWidth) + ROW_GAP;
  };

  drawPdfWatermark(doc, PAGE_WIDTH, PAGE_HEIGHT);

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(16);
  doc.text('Laporan Pengiraan Faraid', MARGIN, y);
  y += LINE_HEIGHT * 2;

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(10);
  addText(`Tarikh: ${formatPdfDate(new Date())}`);
  addText(`Jantina Simati: ${formatDeceasedGender(deceased)}`);
  addText(`Jumlah Harta Pusaka: ${formatCurrency(result.totalEstate)}`);

  addSectionTitle('1. Senarai Waris');
  const presentHeirs = getPresentHeirs(heirs);
  if (presentHeirs.length === 0) {
    addText('Tiada waris dipilih.');
  } else {
    for (const heir of presentHeirs) {
      addText(`• ${heir.label}`, 4);
    }
  }

  addSectionTitle('2. Harta Pusaka');
  const assetsWithValue = getAssetsWithValue(assets);
  if (assetsWithValue.length === 0) {
    addText('Tiada harta pusaka dengan nilai.');
  } else {
    for (const asset of assetsWithValue) {
      const label = ASSET_TYPE_LABELS[asset.type].ms;
      const description = asset.description?.trim();
      const detail = description ? `${label} — ${description}` : label;
      addText(`• ${detail}: ${formatCurrency(asset.value)}`, 4);
    }
    addText(`Jumlah: ${formatCurrency(result.totalEstate)}`);
  }

  addSectionTitle('3. Pengiraan');
  const pengiraanRows = expandPengiraanRows(result);
  const isBaitulmalOnly =
    pengiraanRows.length === 0 && result.remainderToBaitulmal > 0;
  const cols = getPengiraanColumnPositions(MARGIN);

  ensureSpace(LINE_HEIGHT * 2);
  doc.setFont('helvetica', 'bold');
  doc.text('Waris', MARGIN, y);
  doc.text('Bahagian', cols.bahagianX, y);
  doc.text('Pecahan', cols.pecahanX, y);
  doc.text('Jumlah', cols.jumlahX, y);
  y += LINE_HEIGHT;
  doc.setFont('helvetica', 'normal');

  const tableRows: PengiraanPdfRow[] = [...pengiraanRows];
  if (isBaitulmalOnly) {
    tableRows.push({
      displayLabel: 'BAITULMAL',
      bahagian: 'Baki',
      fraction: '1/1',
      amount: result.remainderToBaitulmal,
    });
  }

  for (const row of tableRows) {
    const warisHeight = measureWrappedTextHeight(doc, row.displayLabel, cols.warisMaxWidth);
    ensureSpace(warisHeight + ROW_GAP);
    y = drawPengiraanRow(doc, row, MARGIN, cols, y) + ROW_GAP;
  }

  const grandTotal = isBaitulmalOnly
    ? result.remainderToBaitulmal
    : pengiraanRows.reduce((sum, row) => sum + row.amount, 0);

  ensureSpace(LINE_HEIGHT * 2);
  doc.setFont('helvetica', 'bold');
  doc.text('JUMLAH KESELURUHAN', MARGIN, y);
  doc.text(formatCurrency(grandTotal), cols.jumlahX, y);
  y += LINE_HEIGHT * 1.5;
  doc.setFont('helvetica', 'normal');

  const blockedShares = result.shares.filter((share) => share.isBlocked);
  if (blockedShares.length > 0) {
    addSectionTitle('Waris Terdinding (Mahjub)');
    for (const share of blockedShares) {
      addText(`• ${share.heirLabel}`, 4);
    }
  }

  if (result.remainderToBaitulmal > 0 && !isBaitulmalOnly) {
    addSectionTitle('Baki Baitulmal');
    addText(formatCurrency(result.remainderToBaitulmal));
  }

  if (result.warnings.length > 0) {
    addSectionTitle('Nota');
    for (const warning of result.warnings) {
      addText(`• ${warning}`, 4);
    }
  }

  const { specialCases } = result;
  if (specialCases.isAul || specialCases.isUmariyyatain) {
    addSectionTitle('Kes Khas');
    if (specialCases.isAul) {
      addText(
        `'Aul: Jumlah bahagian fardu melebihi harta. Asal masalah diselaraskan dari ${specialCases.aulOriginalDenominator} kepada ${specialCases.aulNewDenominator}.`
      );
    }
    if (specialCases.isUmariyyatain) {
      addText(
        "'Umariyyatain/Gharrawain: Ibu mendapat 1/3 daripada baki setelah bahagian suami/isteri."
      );
    }
  }

  const dateStamp = new Date().toISOString().slice(0, 10);
  doc.save(`laporan-faraid-${dateStamp}.pdf`);
}
